import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../../context/Auth";
import styles from "./PricingPage.module.css";
import Navigation from "../Home/Navigation";
import { SEO } from "../SEO";
import seoConfig from "../SEO/seoConfig";
import { getApiUrl } from '../../config/api';
import { configService, FeatureLimits } from '../../services/configService';

type PlanName = "Creator" | "Creator Lite" | "Creator Pro" | "Creator Studio" | "Creator Unlimited";
type PlanFeatures = Record<string, string>;

const calculateMaxUsage = (points: number, pointCost: number): string => {
  if (points === 0) return "❌"; 
  if (points === Infinity) return "Unlimited";
  return `Up to ${Math.floor(points / pointCost)}`;
};

const PLAN_POINTS = {
  "Creator": 0,
  "Creator Trial": 3500,
  "Creator Lite": 7000,
  "Creator Pro": 15000,
  "Creator Studio": 30000,
  "Creator Unlimited": Infinity
};

const FEATURE_POINT_COSTS = {
  "Video Caption Generation": 7,
  "AI Thumbnail Creation": 18,
  "Script Generation": 47,
  "Audio Generation (Narration & Music)": 67,
  "Title & Description Generation": 27,
  "AI Image Generation": 27,
  "AI Prompt Generation": 20,
  "Image Analysis & Description": 27,
  "AI Video Generation": 167,
  "Social Media Direct Sharing": 0,
  "Veo 3 AI Video Generation": 167,
};

// English feature keys (for internal lookups)
const FEATURE_KEYS = {
  videoCaptionGeneration: "Video Caption Generation",
  aiThumbnailCreation: "AI Thumbnail Creation",
  scriptGeneration: "Script Generation",
  audioGeneration: "Audio Generation (Narration & Music)",
  aiMusicGeneration: "AI Music Generation (Soundtrack and effects)",
  titleDescriptionGeneration: "Title & Description Generation",
  aiImageGeneration: "AI Image Generation",
  aiPromptGeneration: "AI Prompt Generation",
  imageAnalysisDescription: "Image Analysis & Description",
  aiVideoGeneration: "AI Video Generation",
  videoExportQuality: "Video Export Quality",
  aiVideoOptimization: "AI Video Optimization",
  aiVideoUpscaling: "AI Video Upscaling (4x)",
  aiImageUpscaling: "AI Image Upscaling",
  socialMediaDirectSharing: "Social Media Direct Sharing",
  postPublishScheduling: "Post/Publish Scheduling",
  veo3VideoGeneration: "Veo 3 AI Video Generation",
  creditPurchasing: "Credit Purchasing",
  teamCollaboration: "Team Collaboration",
  customBranding: "Custom Branding",
  apiAccess: "API Access",
  priorityProcessing: "Priority Processing",
  analyticsDashboard: "Analytics Dashboard",
  dedicatedSupport: "Dedicated Support"
};

export default function PricingPage() {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const [darkMode, setDarkMode] = useState(true);
  const [showComparison, setShowComparison] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [expandedCards, setExpandedCards] = useState<{[key: string]: boolean}>({});
  const [featureLimits, setFeatureLimits] = useState<Record<string, FeatureLimits>>({});
  const [configLoading, setConfigLoading] = useState(true);
  const [trialEligibility, setTrialEligibility] = useState<{
    eligible: boolean;
    reason: string;
  }>({ eligible: true, reason: '' });

  // Feature list with English keys and translated display names
  const allFeatures = [
    { key: 'videoCaptionGeneration', display: t('pricing.features.videoCaptionGeneration') },
    { key: 'aiThumbnailCreation', display: t('pricing.features.aiThumbnailCreation') },
    { key: 'scriptGeneration', display: t('pricing.features.scriptGeneration') },
    { key: 'audioGeneration', display: t('pricing.features.audioGeneration') },
    { key: 'aiMusicGeneration', display: t('pricing.features.aiMusicGeneration') },
    { key: 'titleDescriptionGeneration', display: t('pricing.features.titleDescriptionGeneration') },
    { key: 'aiImageGeneration', display: t('pricing.features.aiImageGeneration') },
    { key: 'aiPromptGeneration', display: t('pricing.features.aiPromptGeneration') },
    { key: 'imageAnalysisDescription', display: t('pricing.features.imageAnalysisDescription') },
    { key: 'aiVideoGeneration', display: t('pricing.features.aiVideoGeneration') },
    { key: 'videoExportQuality', display: t('pricing.features.videoExportQuality') },
    { key: 'aiVideoOptimization', display: t('pricing.features.aiVideoOptimization') },
    { key: 'aiVideoUpscaling', display: t('pricing.features.aiVideoUpscaling') },
    { key: 'aiImageUpscaling', display: t('pricing.features.aiImageUpscaling') },
    { key: 'socialMediaDirectSharing', display: t('pricing.features.socialMediaDirectSharing') },
    { key: 'postPublishScheduling', display: t('pricing.features.postPublishScheduling') },
    { key: 'veo3VideoGeneration', display: t('pricing.features.veo3VideoGeneration') },
    { key: 'creditPurchasing', display: t('pricing.features.creditPurchasing') },
    { key: 'teamCollaboration', display: t('pricing.features.teamCollaboration') },
    { key: 'customBranding', display: t('pricing.features.customBranding') },
    { key: 'apiAccess', display: t('pricing.features.apiAccess') },
    { key: 'priorityProcessing', display: t('pricing.features.priorityProcessing') },
    { key: 'analyticsDashboard', display: t('pricing.features.analyticsDashboard') },
    { key: 'dedicatedSupport', display: t('pricing.features.dedicatedSupport') }
  ];

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setConfigLoading(true);
        const planNames = ["Creator", "Creator Lite", "Creator Pro", "Creator Studio", "Creator Unlimited"];
        const limitsPromises = planNames.map(async (planName) => {
          const limits = await configService.getFeatureLimits(planName);
          return { planName, limits };
        });
        
        const allLimits = await Promise.all(limitsPromises);
        const limitsMap: Record<string, FeatureLimits> = {};
        
        allLimits.forEach(({ planName, limits }) => {
          limitsMap[planName] = limits;
        });
        
        setFeatureLimits(limitsMap);
      } catch (error) {
        console.error('Failed to load feature limits:', error);
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfig();
  }, []);

  // Check trial eligibility for authenticated users
  useEffect(() => {
    if (isAuthenticated) {
      checkTrialEligibility();
    }
  }, [isAuthenticated]);

  const checkTrialEligibility = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/billing/trial/eligibility'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTrialEligibility(data);
    } catch (error) {
      console.error('Failed to check trial eligibility:', error);
      setTrialEligibility({ eligible: false, reason: t('pricing.plans.trial.eligibilityError') });
    }
  };

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, []);

  const toggleCardExpansion = (planName: string) => {
    setExpandedCards(prev => ({
      ...prev,
      [planName]: !prev[planName]
    }));
  };

  const hasVeoAccess = () => {
    if (!isAuthenticated || !user?.plan) return false;
    return user.plan !== 'creator' && user.plan !== 'free';
  };

  const getVeoButtonContent = (creditPack: string) => {
    if (!isAuthenticated) {
      return <p style={{ color: '#666', fontWeight: 'bold' }}>{t('pricing.veoSection.loginRequiredButton')}</p>;
    }
    
    if (!hasVeoAccess()) {
      return <p style={{ color: '#ff6b6b', fontWeight: 'bold' }}>{t('pricing.veoSection.onlyPaidPlans')}</p>;
    }
    
    if (loading === `veo-${creditPack}`) {
      return <p style={{ color: '#06b6d4', fontWeight: 'bold' }}>{t('pricing.veoSection.processingButton')}</p>;
    }
    
    return <p style={{ color: '#06b6d4', fontWeight: 'bold' }}>{t('pricing.veoSection.clickToPurchase')}</p>;
  };

  // Get plan value using English feature key
  const getPlanValue = (planName: string, featureKey: string): string => {
    if (configLoading) {
      return t('pricing.loading');
    }

    // Convert feature key to English name for lookups
    const featureName = FEATURE_KEYS[featureKey as keyof typeof FEATURE_KEYS];
    
    // Trial plan has same features as Creator Lite
    if (planName === "Creator Trial") {
      planName = "Creator Lite";
    }
    
    if (planName === "Creator (Free)" || planName === "Creator (Gratuit)") {
      const creatorLimits: Record<string, string> = {
        "Video Caption Generation": `5 ${t('pricing.values.perMonth')}`,
        "AI Thumbnail Creation": `20 ${t('pricing.values.perMonth')}`,
        "Script Generation": `5 ${t('pricing.values.perMonth')}`,
        "Audio Generation (Narration & Music)": `5 ${t('pricing.values.perMonth')}`,
        "AI Music Generation (Soundtrack and effects)": `5 ${t('pricing.values.perMonth')}`,
        "Title & Description Generation": `5 ${t('pricing.values.perMonth')}`,
        "AI Image Generation": `20 ${t('pricing.values.perMonth')}`,
        "AI Prompt Generation": t('pricing.values.limited'),
        "Image Analysis & Description": "❌",
        "AI Video Generation": `5 ${t('pricing.values.perMonth')}`,
        "AI Video Optimization": "❌",
        "AI Video Upscaling (4x)": "❌",
        "AI Image Upscaling": "❌",
        "Social Media Direct Sharing": "❌",
        "Post/Publish Scheduling": "❌",
        "Veo 3 AI Video Generation": "❌",
        "Credit Purchasing": "❌",
        "Team Collaboration": "❌",
        "Custom Branding": "❌",
        "API Access": "❌",
        "Priority Processing": "❌",
        "Analytics Dashboard": "❌",
        "Dedicated Support": t('pricing.values.community')
      };
      return creatorLimits[featureName] || "❌";
    }
    
    if (featureName === "Credit Purchasing") {
      return `✅ ${t('pricing.values.available')}`;
    }
    
    if (featureName === "Veo 3 AI Video Generation") {
      return `✅ ${t('pricing.values.addOnAvailable')}`;
    }
    
    if (featureName === "Team Collaboration") {
      const teamSizes: Record<string, string> = {
        "Creator (Free)": "❌",
        "Creator (Gratuit)": "❌",
        "Creator Lite": "❌",
        "Creator Pro": `2 ${t('pricing.values.members')}`,
        "Creator Studio": `5 ${t('pricing.values.members')}`,
        "Creator Unlimited": `10 ${t('pricing.values.members')}`
      };
      return teamSizes[planName] || "❌";
    }
    
    if (featureName === "Custom Branding") {
      const branding: Record<string, string> = {
        "Creator (Free)": "❌",
        "Creator (Gratuit)": "❌",
        "Creator Lite": "❌",
        "Creator Pro": "❌",
        "Creator Studio": "✅",
        "Creator Unlimited": `✅ + ${t('pricing.values.whiteLabel')}`
      };
      return branding[planName] || "❌";
    }
    
    if (["API Access", "Priority Processing", "Analytics Dashboard"].includes(featureName)) {
      return `🔜 ${t('pricing.values.comingSoon')}`;
    }
    
    if (featureName === "Dedicated Support") {
      const support: Record<string, string> = {
        "Creator (Free)": t('pricing.values.community'),
        "Creator (Gratuit)": t('pricing.values.community'),
        "Creator Lite": t('pricing.values.email'),
        "Creator Pro": t('pricing.values.emailChat'),
        "Creator Studio": `🔜 ${t('pricing.values.comingSoon')}`,
        "Creator Unlimited": `🔜 ${t('pricing.values.comingSoon')}`
      };
      return support[planName] || t('pricing.values.community');
    }
    
    if (featureName === "Social Media Direct Sharing") {
      const socialFeatures: Record<string, string> = {
        "Creator (Free)": "❌",
        "Creator (Gratuit)": "❌",
        "Creator Lite": `✅ ${t('pricing.socialSharing.basic')}`,
        "Creator Pro": `✅ ${t('pricing.socialSharing.advancedScheduling4')}`,
        "Creator Studio": `✅ ${t('pricing.socialSharing.advancedSchedulingUnlimited')}`,
        "Creator Unlimited": `✅ ${t('pricing.socialSharing.advancedWhiteLabel')}`
      };
      return socialFeatures[planName] || "❌";
    }
    
    if (featureName === "Post/Publish Scheduling") {
      const schedulingFeatures: Record<string, string> = {
        "Creator (Free)": "❌",
        "Creator (Gratuit)": "❌",
        "Creator Lite": "❌",
        "Creator Pro": `✅ ${t('pricing.values.available')}`,
        "Creator Studio": `✅ ${t('pricing.values.advancedScheduling')}`,
        "Creator Unlimited": `✅ ${t('pricing.values.advancedScheduling')}`
      };
      return schedulingFeatures[planName] || "❌";
    }
    
    if (featureName === "AI Image Upscaling") {
      const imageUpscaling: Record<string, string> = {
        "Creator (Free)": "❌",
        "Creator (Gratuit)": "❌",
        "Creator Lite": `✅ ${t('pricing.values.available')}`,
        "Creator Pro": `✅ ${t('pricing.values.available')}`,
        "Creator Studio": `✅ ${t('pricing.values.available')}`,
        "Creator Unlimited": `✅ ${t('pricing.values.available')}`
      };
      return imageUpscaling[planName] || "❌";
    }
    
    if (featureName === "AI Video Optimization") {
      const videoOptimization: Record<string, string> = {
        "Creator (Free)": "❌",
        "Creator (Gratuit)": "❌",
        "Creator Lite": `✅ ${t('pricing.values.advancedPrompts')}`,
        "Creator Pro": `✅ ${t('pricing.values.advancedPrompts')}`,
        "Creator Studio": `✅ ${t('pricing.values.advancedPrompts')}`,
        "Creator Unlimited": `✅ ${t('pricing.values.advancedPrompts')}`
      };
      return videoOptimization[planName] || "❌";
    }
    
    if (featureName === "Video Export Quality") {
      const exportQuality: Record<string, string> = {
        "Creator (Free)": t('pricing.exportQuality.sd'),
        "Creator (Gratuit)": t('pricing.exportQuality.sd'),
        "Creator Lite": t('pricing.exportQuality.hd'),
        "Creator Pro": t('pricing.exportQuality.2k'),
        "Creator Studio": t('pricing.exportQuality.4k'),
        "Creator Unlimited": t('pricing.exportQuality.4k')
      };
      return exportQuality[planName] || t('pricing.exportQuality.sd');
    }
    
    if (featureLimits[planName] && featureLimits[planName][featureName]) {
      return featureLimits[planName][featureName];
    }
    
    return "❌";
  };

  const generatePlanFeatures = (planName: string) => {
    if (configLoading) {
      return {
        essentialFeatures: [t('pricing.loading')],
        additionalFeatures: [t('pricing.loading')]
      };
    }

    const getPointBadge = (plan: string): string => {
      if (plan === t('pricing.plans.trial.name') || plan === "Creator Trial") return t('pricing.plans.trial.pointBadge');
      if (plan === t('pricing.plans.creatorLite.name') || plan === "Creator Lite") return t('pricing.plans.creatorLite.pointBadge');
      if (plan === t('pricing.plans.creatorPro.name') || plan === "Creator Pro") return t('pricing.plans.creatorPro.pointBadge');
      if (plan === t('pricing.plans.creatorStudio.name') || plan === "Creator Studio") return t('pricing.plans.creatorStudio.pointBadge');
      if (plan === t('pricing.plans.creatorUnlimited.name') || plan === "Creator Unlimited") return t('pricing.plans.creatorUnlimited.pointBadge');
      return '';
    };

    const coreFeatureKeys = [
      'aiVideoGeneration',
      'videoCaptionGeneration',
      'audioGeneration',
      'aiThumbnailCreation',
      'titleDescriptionGeneration',
      'videoExportQuality',
      'socialMediaDirectSharing',
      'dedicatedSupport'
    ];

    const additionalFeatureKeys = [
      'scriptGeneration',
      'aiImageGeneration',
      'aiVideoGeneration',
      'aiMusicGeneration',
      'aiPromptGeneration',
      'imageAnalysisDescription',
      'aiVideoOptimization',
      'aiVideoUpscaling',
      'aiImageUpscaling',
      'postPublishScheduling',
      'creditPurchasing',
      'teamCollaboration',
      'customBranding'
    ];

    const essentialFeatures = [];
    
    const pointBadge = getPointBadge(planName);
    if (pointBadge) {
      essentialFeatures.push(pointBadge);
    }
    
    coreFeatureKeys.forEach(featureKey => {
      const featureDisplay = t(`pricing.features.${featureKey}`);
      const value = getPlanValue(planName, featureKey);
      
      if (featureKey === 'socialMediaDirectSharing' && value === "❌") {
        essentialFeatures.push(`❌ ${t('pricing.values.noSocialSharing')}`);
        return;
      }
      if (featureKey === 'dedicatedSupport') {
        const supportText = value === t('pricing.values.community') 
          ? t('pricing.values.communitySupport')
          : `${value} ${t('pricing.values.support')}`;
        essentialFeatures.push(supportText);
        return;
      }
      if (featureKey === 'videoExportQuality') {
        essentialFeatures.push(`📹 ${value === "❌" ? t('pricing.exportQuality.sd') : value} ${t('pricing.values.videoExports')}`);
        return;
      }
      essentialFeatures.push(`${featureDisplay}: ${value}`);
    });

    const additionalFeatures = additionalFeatureKeys.map(featureKey => {
      const featureDisplay = t(`pricing.features.${featureKey}`);
      const value = getPlanValue(planName, featureKey);
      if (value === "❌") {
        return `❌ ${featureDisplay}`;
      }
      return `${featureDisplay}: ${value}`;
    }).filter(feature => !feature.startsWith("❌"));

    return { essentialFeatures, additionalFeatures };
  };
  const handleVeoCreditPurchase = async (creditPack: '5-credits' | '10-credits' | '30-credits' | '60-credits') => {
    if (!isAuthenticated) {
      window.location.href = '/login-page?redirect=pricing';
      return;
    }

    if (!user?.plan || user.plan === 'creator' || user.plan === 'free') {
      setError('Veo AI Video Generation requires a paid plan. Please upgrade to Creator Lite or higher first.');
      return;
    }

    setLoading(`veo-${creditPack}`);
    setError('');
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/veo-credits/purchase'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          creditPack: creditPack,
          successUrl: `${window.location.origin}/success?purchase=veo-credits&pack=${creditPack}`,
          cancelUrl: `${window.location.origin}/pricing`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create Veo credits checkout session');
      }

      const { checkoutUrl } = await response.json();
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(null);
    }
  };

  const handleSubscribe = async (planType: 'creator_lite' | 'creator_pro' | 'creator_studio' | 'creator_unlimited') => {
    if (!isAuthenticated) {
      window.location.href = '/login-page?redirect=pricing';
      return;
    }

    setLoading(planType);
    setError('');
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/billing/create-subscription-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          planType: planType,
          successUrl: `${window.location.origin}/dashboard?subscribed=true&plan=${planType}`,
          cancelUrl: `${window.location.origin}/pricing`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { checkoutUrl } = await response.json();
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(null);
    }
  };

  const handleTrialSubscribe = async () => {
    if (!isAuthenticated) {
      window.location.href = '/login-page?redirect=pricing&trial=true';
      return;
    }

    if (!trialEligibility.eligible) {
      setError(trialEligibility.reason || t('pricing.plans.trial.alreadyUsed'));
      return;
    }

    setLoading('trial');
    setError('');
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/billing/create-trial-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          successUrl: `${window.location.origin}/trial-success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/pricing`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create trial checkout');
      }

      const { checkoutUrl } = await response.json();
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(null);
    }
  };

  const getButtonText = (planName: string) => {
    if (!isAuthenticated) {
      return planName === t('pricing.plans.creator.name') || planName === "Creator (Free)" ? t('pricing.buttons.startFree') : t('pricing.buttons.choosePlan');
    }
    
    const currentPlan = user?.plan?.toLowerCase() || 'creator';
    const targetPlan = planName.toLowerCase().replace(/\s+/g, '_');
    
    if (planName === t('pricing.plans.creator.name') || planName === "Creator (Free)") {
      return t('pricing.buttons.startFree');
    }
    
    if (currentPlan === targetPlan || (currentPlan !== 'creator' && currentPlan !== 'free')) {
      return t('pricing.buttons.restartPlan');
    }
    
    return t('pricing.buttons.choosePlan');
  };

  const isCurrentPlan = (planName: string): boolean => {
    if (!isAuthenticated || !user?.plan) return false;
    
    const currentPlan = user.plan.toLowerCase();
    const checkPlan = planName.toLowerCase().replace(/\s+/g, '_').replace(/\(free\)/, '').replace(/\(gratuit\)/, '');
    
    if (planName === t('pricing.plans.creator.name') || planName === "Creator (Free)") {
      return currentPlan === 'creator';
    }
    
    return currentPlan === checkPlan;
  };

  const handlePointCreditPurchase = async (points: number) => {
    if (!isAuthenticated) {
      window.location.href = '/login-page?redirect=pricing';
      return;
    }

    if (!user?.plan || user.plan === 'creator' || user.plan === 'free') {
      setError('Point credit purchases require a paid plan. Please upgrade to Creator Lite or higher first.');
      return;
    }

    setLoading(`points-${points}`);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl('/billing/create-credit-session')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          creditAmount: points,
          successUrl: `${window.location.origin}/success?purchase=credits&amount=${points}`,
          cancelUrl: `${window.location.origin}/pricing`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create checkout session');
      }

      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(null);
    }
  };

  // Get plan names using English internally
  const pricingPlans = [
    // Show trial plan: ALWAYS for logged out, ONLY if eligible for logged in
    ...(!isAuthenticated || trialEligibility.eligible ? [{
      name: "Creator Trial",
      displayName: t('pricing.plans.trial.displayName'),
      price: t('pricing.plans.trial.price'),
      thenPrice: t('pricing.plans.trial.thenPrice'),
      popular: false,
      badge: t('pricing.plans.trial.badge'),
      isTrial: true
    }] : []),
    // Show Free plan: ONLY for logged in users who are NOT eligible for trial
    ...(isAuthenticated && !trialEligibility.eligible ? [{
      name: "Creator (Free)",
      displayName: t('pricing.plans.creator.name'),
      price: t('pricing.plans.creator.price'),
      popular: false
    }] : []),
    { name: "Creator Lite", displayName: t('pricing.plans.creatorLite.name'), price: t('pricing.plans.creatorLite.price'), popular: false },
    { name: "Creator Pro", displayName: t('pricing.plans.creatorPro.name'), price: t('pricing.plans.creatorPro.price'), popular: true },
    { name: "Creator Studio", displayName: t('pricing.plans.creatorStudio.name'), price: t('pricing.plans.creatorStudio.price'), popular: false },
    { name: "Creator Unlimited", displayName: t('pricing.plans.creatorUnlimited.name'), price: t('pricing.plans.creatorUnlimited.price'), popular: false }
  ];

  return (
    <div className={styles.outerContainer}>
      <SEO
        title={seoConfig.pricing.title}
        description={seoConfig.pricing.description}
        keywords={seoConfig.pricing.keywords}
        url="https://kingdomstudio.pro/pricing"
      />
      <Navigation></Navigation>
      <div className={`${styles.container} ${darkMode ? styles.darkMode : ''}`}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>{t('pricing.pageTitle')}</h1>
          </div>
          <div className={styles.buttonGroup}>
            <button 
              className={styles.button}
              onClick={() => setShowComparison(!showComparison)}
            >
              {showComparison ? t('pricing.hideComparison') : t('pricing.showComparison')}
            </button>
          </div>
        </div>

        {error && (
          <div className={styles.errorBanner}>
            <p>❌ {error}</p>
            <button onClick={() => setError('')} className={styles.closeError}>{t('pricing.errorClose')}</button>
          </div>
        )}
        
        {!showComparison ? (
          <div className={styles.cardsGrid}>
            {pricingPlans.map((plan, index) => (
              <div 
                key={index} 
                className={`${styles.card} ${darkMode ? styles.cardDark : ''} ${plan.popular ? styles.popularCard : ''} ${(plan as any).isTrial ? styles.trialCard : ''}`}
              >
                {(plan.popular || isCurrentPlan(plan.name) || (plan as any).badge) && (
                  <div className={`${styles.badge} ${plan.popular ? styles.popularBadge : ''} ${(plan as any).isTrial ? styles.trialBadge : ''}`}>
                    {(plan as any).badge || (isCurrentPlan(plan.name) ? t('pricing.currentPlan') : (plan.popular ? t('pricing.plans.creatorPro.popular') : ''))}
                  </div>
                )}
                <div className={styles.cardContent}>
                  <h2 className={styles.planName}>{plan.displayName}</h2>
                  <p className={styles.planPrice}>
                    {plan.price}
                    {(plan as any).thenPrice && (
                      <span className={styles.thenPrice}> {(plan as any).thenPrice}</span>
                    )}
                  </p>
                  <ul className={styles.featuresList}>
                    {generatePlanFeatures(plan.name).essentialFeatures.map((feature, idx) => (
                      <li key={idx} className={styles.featureItem}>
                        {feature.includes('MONTHLY POINTS') || feature.includes('TRIAL POINTS') || feature.includes('POINTS MENSUELS') || feature.includes('ILLIMITÉS') ? (
                          <span className={styles.highlightFeature}>{feature}</span>
                        ) : feature.startsWith('💳') ? (
                          <span className={styles.creditFeature}>{feature}</span>
                        ) : (
                          <>
                            <span className={styles.checkmark}>✅</span> {feature}
                          </>
                        )}
                      </li>
                    ))}
                    {expandedCards[plan.name] && generatePlanFeatures(plan.name).additionalFeatures.map((feature, idx) => (
                      <li key={`additional-${idx}`} className={styles.featureItem}>
                        {feature.startsWith('✨') ? (
                          <span className={styles.highlightFeature}>{feature}</span>
                        ) : feature.startsWith('💳') ? (
                          <span className={styles.creditFeature}>{feature}</span>
                        ) : (
                          <>
                            <span className={styles.checkmark}>✅</span> {feature}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                  
                  {/* Auto-Renewal Disclosure for Trial Plan */}
                  {(plan as any).isTrial && (
                    <div className={styles.autoRenewDisclosure}>
                      <p>{t('pricing.plans.trial.autoRenewDisclosure')}</p>
                    </div>
                  )}
                  
                  {generatePlanFeatures(plan.name).additionalFeatures && generatePlanFeatures(plan.name).additionalFeatures.length > 0 && (
                    <button 
                      className={styles.showMoreButton}
                      onClick={() => toggleCardExpansion(plan.name)}
                    >
                      {expandedCards[plan.name] ? t('pricing.buttons.showLess') : `${t('pricing.buttons.showMore')} (${generatePlanFeatures(plan.name).additionalFeatures.length})`}
                    </button>
                  )}
                  <button 
                    className={`${styles.button} ${styles.fullWidth} ${plan.popular ? styles.popularButton : ''} ${(plan as any).isTrial ? styles.trialButton : ''}`}
                    onClick={() => {
                      if (plan.name === "Creator Trial") {
                        handleTrialSubscribe();
                      } else if (plan.name === "Creator (Free)") {
                        if (!isAuthenticated) {
                          window.location.href = '/login-page';
                        } else {
                          window.location.href = '/dashboard';
                        }
                      } else if (plan.name === "Creator Lite") {
                        handleSubscribe('creator_lite');
                      } else if (plan.name === "Creator Pro") {
                        handleSubscribe('creator_pro');
                      } else if (plan.name === "Creator Studio") {
                        handleSubscribe('creator_studio');
                      } else if (plan.name === "Creator Unlimited") {
                        handleSubscribe('creator_unlimited');
                      }
                    }}
                    disabled={loading === plan.name.toLowerCase().replace(/\s+/g, '_') || loading === 'trial'}
                  >
                    {loading === 'trial' && plan.name === "Creator Trial" ? t('pricing.processing') : 
                     (loading === plan.name.toLowerCase().replace(/\s+/g, '_') ? t('pricing.processing') : 
                      (plan.name === "Creator Trial" ? t('pricing.buttons.tryForOne') : getButtonText(plan.name)))}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.comparisonTable}>
              <thead>
                <tr className={styles.tableHeader}>
                  <th className={styles.tableCell}>{t('pricing.comparison.feature')}</th>
                  {pricingPlans.map((plan, index) => (
                    <th key={index} className={styles.tableCell}>{plan.displayName}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allFeatures.map((feature, idx) => (
                  <tr key={idx} className={styles.tableRow}>
                    <td className={styles.tableCell}>{feature.display}</td>
                    {pricingPlans.map((plan, planIdx) => (
                      <td key={planIdx} className={`${styles.tableCell} ${styles.textCenter}`}>
                        {getPlanValue(plan.name, feature.key)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div id="veo-section" className={styles.veoAddonSection}>
          <h3>{t('pricing.veoSection.title')}</h3>
          <p>{t('pricing.veoSection.subtitle')}</p>
          
          {!hasVeoAccess() && (
            <div style={{
              background: 'linear-gradient(135deg, #ff6b6b, #ffd93d)',
              color: '#fff',
              padding: '16px',
              borderRadius: '12px',
              marginBottom: '20px',
              textAlign: 'center',
              fontWeight: 'bold'
            }}>
              {t('pricing.veoSection.requiresPaidPlan')}
              {!isAuthenticated && t('pricing.veoSection.loginRequired')}
            </div>
          )}
          
          <div className={styles.veoOptions}>
            {['5credits', '10credits', '30credits', '60credits'].map((pack) => (
              <div 
                key={pack}
                className={`${styles.veoOption} ${hasVeoAccess() ? styles.clickable : styles.disabled}`}
                onClick={hasVeoAccess() ? () => handleVeoCreditPurchase(pack as any) : undefined}
                style={{ 
                  cursor: hasVeoAccess() ? 'pointer' : 'not-allowed',
                  opacity: hasVeoAccess() ? 1 : 0.6
                }}
              >
                <h4>{t(`pricing.veoSection.packs.${pack}.title`)}</h4>
                <p className={styles.veoPrice}>{t(`pricing.veoSection.packs.${pack}.price`)}</p>
                <p>{t(`pricing.veoSection.packs.${pack}.description`)}</p>
                {getVeoButtonContent(pack)}
              </div>
            ))}
          </div>
          <div className={styles.veoBundle}>
            <h4>{t('pricing.veoSection.howItWorksTitle')}</h4>
            <p>{t('pricing.veoSection.howItWorksDescription')}</p>
          </div>
        </div>

        {isAuthenticated && user?.plan !== 'creator' && (
          <div id="points-section" className={styles.creditSection}>
            <div className={styles.creditHeader}>
              <h2>{t('pricing.pointsSection.title')}</h2>
              <p>{t('pricing.pointsSection.subtitle')}</p>
            </div>
            
            <div className={styles.pointCreditGrid}>
              <div className={styles.creditCard}>
                <div className={styles.creditBadge}>{t('pricing.pointsSection.badgeLabel')}</div>
                <h3>{t('pricing.pointsSection.cardTitle')}</h3>
                <p className={styles.creditDescription}>{t('pricing.pointsSection.cardDescription')}</p>
                
                <div className={styles.pointOptions}>
                  {[3000, 6500, 16000, 35000].map((points) => (
                    <div key={points} className={styles.pointOption}>
                      <div className={styles.pointDetails}>
                        <span className={styles.pointAmount}>{points.toLocaleString()} Points</span>
                        <span className={styles.pointEquivalent}>
                          ~{Math.round(points/27)} {t('pricing.pointsSection.imagesOr')} ~{Math.round(points/167)} {t('pricing.pointsSection.videoGens')}
                        </span>
                      </div>
                      <button 
                        className={styles.pointPurchaseBtn}
                        onClick={() => handlePointCreditPurchase(points)}
                        disabled={loading === `points-${points}`}
                      >
                        {loading === `points-${points}` ? t('pricing.processing') : t('pricing.pointsSection.purchaseButton')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={styles.faqSection}>
          <h3>{t('pricing.faq.title')}</h3>
          <div className={styles.faqGrid}>
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <div key={num} className={styles.faqItem}>
                <h4>{t(`pricing.faq.q${num}Question`)}</h4>
                <p>{t(`pricing.faq.q${num}Answer`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
